import axios from 'axios'
import { CloseAccountWriteResponse } from 'src/queries/creditCards/closeAccount/closeAccount.models'
import { getResponseData } from 'src/queries/network.utils'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'
import { BackendErrorPayload } from 'src/utils/error.utils'

/**
 * Pure network fn for permanently closing a credit-card-backed account
 * (`PUT /creditcards/:id/close`). Replaces the `putCloseAccountAndCloseModal`
 * thunk's axios call, which used `selectAuthedJsonApiRequestConfig` —
 * `RequestConfig.Default` here. No request body, matching the original
 * `undefined` PUT body.
 */
export const closeAccount = async (
  creditCardId: string,
): Promise<CloseAccountWriteResponse> =>
  getResponseData(
    axios.put<CloseAccountWriteResponse>(
      `/creditcards/${creditCardId}/close`,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )

/**
 * Recover the raw backend error payload a mutation failure carries.
 * `normalizeQueryError` (network.utils.ts) stashes the original AxiosError on
 * the non-enumerable `QueryError.originalError`; `closeAccountContent.reducer.ts`'s
 * `topLevelError` (fed via `parseBackendErrors`) is the reader.
 */
export const recoverBackendErrorPayload = (
  errorSource: unknown,
): BackendErrorPayload<string> => {
  if (axios.isAxiosError(errorSource) && errorSource.response?.data) {
    return errorSource.response.data
  }
  if (
    errorSource !== null &&
    typeof errorSource === 'object' &&
    'error' in errorSource
  ) {
    return errorSource as BackendErrorPayload<string>
  }
  return { error: 'Sorry, something went wrong. Please try again later.' }
}
